// /accounts/$accountSlug/tenants/$tenantSlug/ (index) — detail card +
// vault_sync_payload viewer. The header (title + VaultSyncPill) and
// the tab strip both live on TenantLayout one level up, so they
// persist across /secrets, /ha-instances, etc.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { tenants } from "../../api/endpoints/tenants";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { JSONViewer } from "../../components/ui/JSONViewer";

import { VaultSyncPill } from "./VaultSyncPill";

interface TenantDetailPageProps {
  accountSlug: string;
  tenantSlug: string;
}

export function TenantDetailPage({
  accountSlug,
  tenantSlug,
}: TenantDetailPageProps) {
  const queryClient = useQueryClient();
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.tenant(accountSlug, tenantSlug),
    queryFn: () => tenants.get(accountSlug, tenantSlug).then((r) => r.data),
  });

  const resync = useMutation({
    mutationFn: () => tenants.vaultResync(accountSlug, tenantSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.tenant(accountSlug, tenantSlug),
      });
    },
  });

  if (q.isPending) {
    return <p style={{ color: "var(--text-dim)" }}>Loading…</p>;
  }
  if (q.error) {
    return <ErrorBanner error={q.error} title="Failed to load tenant" />;
  }
  if (!q.data) return null;

  const showResync = q.data.vault_sync_status === "error";
  // resync.data is set after a 200 even when Vault still failed — surface
  // that via vaultSyncFailed so the operator sees the click landed but
  // the underlying problem is unresolved (still in `error` state).
  const stillFailedAfterRetry =
    resync.isSuccess && resync.data?.vaultSyncFailed === true;

  return (
    <section style={card}>
      <dl style={dl}>
        <dt>Vault path</dt>
        <dd><code>{q.data.vault_path}</code></dd>
        <dt>Vault sync</dt>
        <dd>
          <VaultSyncPill status={q.data.vault_sync_status} />
          {q.data.vault_sync_last_error && (
            <span style={{ color: "var(--crit)", marginLeft: 8 }}>
              {q.data.vault_sync_last_error}
            </span>
          )}
          {showResync && (
            <button
              type="button"
              onClick={() => resync.mutate()}
              disabled={resync.isPending}
              style={resyncBtn}
            >
              {resync.isPending ? "Retrying…" : "Retry Vault sync"}
            </button>
          )}
          {stillFailedAfterRetry && (
            <span style={{ color: "var(--crit)", marginLeft: 8 }}>
              Retry attempted; Vault still rejected.
            </span>
          )}
        </dd>
        {q.data.vault_sync_last_attempt_at && (
          <>
            <dt>Last sync attempt</dt>
            <dd>{new Date(q.data.vault_sync_last_attempt_at).toLocaleString()}</dd>
          </>
        )}
        <dt>Status</dt>
        <dd>{q.data.status}</dd>
        {q.data.commissioning_phase && (
          <>
            <dt>Commissioning phase</dt>
            <dd>{q.data.commissioning_phase}</dd>
          </>
        )}
        <dt>Support email</dt>
        <dd>{q.data.support_email ?? "—"}</dd>
        <dt>Locale / timezone</dt>
        <dd>
          {q.data.locale} / {q.data.timezone}
        </dd>
        <dt>Activated</dt>
        <dd>
          {q.data.activated_at
            ? new Date(q.data.activated_at).toLocaleString()
            : "—"}
        </dd>
        <dt>Created / updated</dt>
        <dd>
          {new Date(q.data.created_at).toLocaleString()} ·{" "}
          {new Date(q.data.updated_at).toLocaleString()}
        </dd>
      </dl>

      <div style={{ marginTop: 16 }}>
        <JSONViewer
          value={q.data.vault_sync_payload}
          title="Vault payload (dual-write retry source)"
          emptyLabel="(cleared — Vault is in sync)"
        />
      </div>

      {resync.error && (
        <div style={{ marginTop: 12 }}>
          <ErrorBanner error={resync.error} title="Resync request failed" />
        </div>
      )}
    </section>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 16,
};

const dl: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  gap: "6px 16px",
  margin: 0,
  fontSize: "0.9rem",
};

const resyncBtn: React.CSSProperties = {
  marginLeft: 12,
  padding: "2px 10px",
  fontSize: "0.8rem",
  background: "var(--surface-2)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  cursor: "pointer",
};
